import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatPesos, parsePesos } from '@dayon/money';

import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';

/**
 * Add a reward to the Points catalogue — the control that had no route.
 *
 * `points.reward` had no writer, so the button was drawn hard-disabled and the
 * screen said the catalogue had been read-only since the seed. A new reward meant
 * hand-written INSERT on a table whose rows carry a cash face value players
 * redeem points against.
 *
 * THE FORM CHANGES SHAPE WITH THE KIND, and it says why rather than just hiding
 * a box. Merch is shipped, so it has stock and no cash value; cash and bonus are
 * issued, so they have a cash value and no stock. Both directions are real rules
 * the database now enforces (`reward_shape_matches_kind`): a stock figure on a
 * cash reward reads as a limit nothing decrements, and a cash value on merch is a
 * face value nothing pays out sitting in the column a finance report adds up.
 */
const KINDS = [
  {
    key: 'CASH',
    label: 'Cash — paid to the player',
    blurb:
      'Issued, not shipped. It carries a cash value and no stock: nothing decrements a cash reward, so a number there would read as a limit somebody is enforcing.',
  },
  {
    key: 'BONUS',
    label: 'Bonus — a free bet or credit',
    blurb:
      'Issued like cash, and counted the same way. It carries a cash value and no stock.',
  },
  {
    key: 'MERCH',
    label: 'Merch — a physical item',
    blurb:
      'Shipped, so it carries units on hand. Approving a redemption takes one unit; at zero the request is refused by name and the player keeps their points. It carries no cash value — nothing pays one out for merch.',
  },
] as const;

const REASONS = [
  { code: 'NEW_CAMPAIGN', label: 'New campaign' },
  { code: 'SEASONAL_OFFER', label: 'Seasonal offer' },
  { code: 'PARTNER_PROMOTION', label: 'Partner promotion' },
  { code: 'REPLACING_WITHDRAWN_REWARD', label: 'Replacing a withdrawn reward' },
  { code: 'COMMERCIAL_DECISION', label: 'Commercial decision' },
] as const;

export function AddRewardDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const { brands } = useSession();

  const [name, setName] = useState('');
  const [kind, setKind] = useState<string>('CASH');
  const [pointsCost, setPointsCost] = useState('');
  /** Typed in pesos, sent in centavos — like every money field in this console. */
  const [cashValue, setCashValue] = useState('');
  const [stock, setStock] = useState('');
  const [active, setActive] = useState(true);
  /** Empty means every brand — the same thing a null brand_id means on a promotion. */
  const [brandId, setBrandId] = useState('');
  const [reasonCode, setReasonCode] = useState<string>(REASONS[0].code);
  const [note, setNote] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setKind('CASH');
    setPointsCost('');
    setCashValue('');
    setStock('');
    setActive(true);
    setBrandId('');
    setReasonCode(REASONS[0].code);
    setNote('');
    setFields({});
  }, [open]);

  const isMerch = kind === 'MERCH';
  const chosen = KINDS.find((k) => k.key === kind)!;

  /* What a player effectively gets per point, shown live. It is the number a
     commercial decision actually turns on, and computing it here beats leaving
     an operator to divide two figures in their head. */
  const centavos = ((): bigint | null => {
    if (isMerch || cashValue.trim() === '') return null;
    /* `parsePesos` REFUSES a malformed amount rather than guessing, and THIS
       RUNS ON EVERY RENDER — including the first, when the field is empty.
       A bare call here threw a RangeError before the screen painted, so the
       whole Points route went blank rather than the field showing a problem.
       Every other money form in the back office guards the same way; this one
       did not. Null is what the two readers below already expect. */
    try {
      return parsePesos(cashValue);
    } catch {
      return null;
    }
  })();
  const costNumber = Number(pointsCost.replace(/[^0-9]/g, ''));
  const perPoint =
    centavos !== null && costNumber > 0 ? Number(centavos) / costNumber : null;

  const submit = async () => {
    const problems: Record<string, string> = {};
    if (name.trim().length < 3) problems['name'] = 'Give the reward a name a player will recognise.';
    if (!/^[1-9][0-9]*$/.test(pointsCost.replace(/[^0-9]/g, '')) || costNumber <= 0) {
      problems['pointsCost'] = 'Enter the points cost as a whole number above zero.';
    }
    if (isMerch) {
      if (!/^[0-9]+$/.test(stock.trim())) {
        problems['stock'] = 'Enter the units on hand — zero is allowed, and means unavailable.';
      }
    } else if (centavos === null || centavos <= 0n) {
      problems['cashValue'] = 'Enter what the player receives, in pesos.';
    }
    if (note.trim().length < 10) {
      problems['note'] = 'Say why this reward is being added — at least 10 characters.';
    }
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }
    setFields({});

    setBusy(true);
    try {
      const result = await api.post<{
        name: string;
        kind: string;
        pointsCost: string;
        active: boolean;
        platformWide: boolean;
        brandId: string | null;
      }>('/api/points/rewards', {
        name: name.trim(),
        kind,
        pointsCost: String(costNumber),
        ...(isMerch ? { stock: Number(stock.trim()) } : { cashValue: String(centavos) }),
        ...(brandId ? { brandId } : {}),
        active,
        reasonCode,
        note: note.trim(),
      });
      toast(
        `${result.name} added at ${result.pointsCost} points${
          result.active ? '' : ', held back and not yet offered'
        }${
          result.platformWide
            ? ' — in every brand’s catalogue'
            : ` — on ${brands.find((b) => b.id === result.brandId)?.name ?? 'one brand'} only`
        }.`,
      );
      onDone();
      onClose();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'The reward was not added',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was saved. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      wide
      title="Add a reward"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} disabled={busy} onClick={() => void submit()}>
            Add the reward
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field
          label="Offered to"
          hint="Every brand is the default, and what every reward created before this choice existed still is."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
            >
              <option value="">Every brand</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} only
                </option>
              ))}
            </Select>
          )}
        </Field>

        <div className="notice notice-quiet">
          <Icon name="storefront" />
          <div>
            {brandId ? (
              <>
                Only <strong>{brands.find((b) => b.id === brandId)?.name}</strong> players will see
                this, and only they can redeem it — a player on another brand who somehow has the
                id is refused, not just kept from seeing it.
              </>
            ) : (
              <>
                <strong>Every player on the platform will see this</strong>, on all brands. Pick a
                brand above if it belongs to one campaign — a reward left on every shelf is one an
                operator finds out about from a player on a brand they did not mean.
              </>
            )}
          </div>
        </div>

        <Field label="Name the player sees" error={fields['name']}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="GCash load ₱200"
            />
          )}
        </Field>

        <Field label="Kind" hint={chosen.blurb}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              {KINDS.map((k) => (
                <option key={k.key} value={k.key}>
                  {k.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <div className="halves">
          <Field
            label="Points cost"
            error={fields['pointsCost']}
            hint="What the player spends. Debited when they request it, and returned in full if the request is refused."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={pointsCost}
                onChange={(e) => setPointsCost(e.target.value)}
                placeholder="1800"
              />
            )}
          </Field>

          {isMerch ? (
            <Field
              label="Units on hand"
              error={fields['stock']}
              hint="Approving one redemption takes one unit. Zero is allowed and means the reward is listed but unavailable — a request against it is refused and the points are not spent."
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                  placeholder="120"
                />
              )}
            </Field>
          ) : (
            <Field
              label="Cash value the player receives"
              error={fields['cashValue']}
              hint="In pesos. This is the figure a finance report counts as the cost of the reward."
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={cashValue}
                  onChange={(e) => setCashValue(e.target.value)}
                  placeholder="200.00"
                />
              )}
            </Field>
          )}
        </div>

        {/* The number the decision actually turns on, computed rather than left
            to be divided in somebody's head. Only for the issued kinds — merch
            has no cash value, so there is no rate to state. */}
        {perPoint !== null ? (
          <div className="notice notice-quiet">
            <Icon name="calculator" />
            <div>
              That is <strong>{formatPesos(BigInt(Math.round(perPoint * 100)))} per 100 points</strong>
              , or {formatPesos(centavos!)} for {costNumber} points. Compare it with the rewards
              already in the catalogue before you offer it — nothing on this platform refuses a rate
              somebody did not mean.
            </div>
          </div>
        ) : null}

        <label className="row" style={{ gap: 'var(--space-2)' }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span>
            Offer it now
            <span className="text-subtle" style={{ display: 'block', fontSize: '11px' }}>
              {/* "No route yet to turn it on later" went stale when POST
                  /api/points/rewards/:id/offered and the card's Offer it button landed. */}
              Unticked, the reward is added and held back: it is in the catalogue, players do not see
              it, and nothing can be redeemed against it until somebody presses{' '}
              <strong>Offer it</strong> on its card in the catalogue.
            </span>
          </span>
        </label>

        <Field
          label="Reason"
          error={fields['reasonCode']}
          hint="A closed set, so the catalogue log can be counted by cause."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
            >
              {REASONS.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Note"
          error={fields['note']}
          hint="At least 10 characters. This is what the next operator reads when they ask why a reward is priced the way it is."
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Christmas campaign — matches the ₱200 free bet already offered."
            />
          )}
        </Field>

        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            <strong>A reward is never deleted.</strong> Redemptions point at it, so removing one
            would leave records of transactions nobody can explain — a reward that should no longer
            be offered is set inactive instead. This is recorded twice in one transaction: against
            the reward, so the catalogue log says who added it and why, and in the platform audit
            trail. Neither can be edited afterwards.
          </div>
        </div>
      </div>
    </Dialog>
  );
}
